import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { KeyRound, Loader2, CheckCircle2, AlertCircle, UserCircle, Check, Mail, Bell, Download, ShieldOff, Trash2, LogOut, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api, ACTIVITY_LABELS, getToken } from "../lib/api";
import { pushSupported, enablePush, disablePush } from "../lib/push";

export default function Settings() {
  const { user, loading, refresh, logout } = useAuth();
  const [nameForm, setNameForm] = useState({ firstName: "", lastName: "" });
  const [nameBusy, setNameBusy] = useState(false);
  const [nameMsg, setNameMsg] = useState<string | null>(null);
  const [nameErr, setNameErr] = useState<string | null>(null);
  useEffect(() => { if (user) setNameForm({ firstName: user.firstName || "", lastName: user.lastName || "" }); }, [user?.id]);
  async function saveName(e: React.FormEvent) {
    e.preventDefault(); setNameBusy(true); setNameMsg(null); setNameErr(null);
    try {
      const r = await api.updateMyName(nameForm.firstName.trim(), nameForm.lastName.trim());
      await refresh();
      setNameMsg(r.reKycTriggered ? "Name updated. Because this is identity information, our team will review the change (re-KYC). Your verified status stays active meanwhile." : "Name updated.");
    } catch (e: any) { setNameErr(e?.message || "Could not update your name."); } finally { setNameBusy(false); }
  }
  const [, navigate] = useLocation();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showNew, setShowNew] = useState(false); // QA Tab3 #10: show/hide toggles
  const [showConf, setShowConf] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Increment 24: change email
  const [newEmail, setNewEmail] = useState("");
  const [emailPw, setEmailPw] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailErr, setEmailErr] = useState<string | null>(null);
  const [emailPending, setEmailPending] = useState<string | null>(null);
  const [emailConfirmUrl, setEmailConfirmUrl] = useState<string | null>(null);

  // Increment 35: email notification preference
  const [emailNotif, setEmailNotif] = useState(true);
  const [emailNotifBusy, setEmailNotifBusy] = useState(false);

  // Increment 36: browser push
  const [pushInfo, setPushInfo] = useState<{ configured: boolean; count: number } | null>(null);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMsg, setPushMsg] = useState<string | null>(null);

  // Increment 40: data rights
  const [delState, setDelState] = useState<{ scheduled: boolean; scheduledAt: string | null; graceDays: number } | null>(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportMsg, setExportMsg] = useState<string | null>(null);
  const [delBusy, setDelBusy] = useState(false);

  useEffect(() => { if (user) api.dataStatus().then((r) => setDelState(r.deletion)).catch(() => {}); }, [user]);

  async function downloadExport() {
    setExportBusy(true); setExportMsg(null);
    try {
      const r = await api.createDataExport();
      const url = api.dataExportUrl(r.downloadPath);
      const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken() ?? ""}` } });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = `kontrataph-data-${r.export.id}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      setExportMsg("Your data export has downloaded.");
    } catch (e: any) { setExportMsg(e?.message || "Could not build your export."); }
    finally { setExportBusy(false); }
  }
  async function requestDeletion() {
    if (!window.confirm("Schedule permanent deletion of your account? You'll have a grace period to cancel before it's erased.")) return;
    setDelBusy(true);
    try { const r = await api.scheduleAccountDeletion(); setDelState({ scheduled: true, scheduledAt: r.scheduledAt, graceDays: r.graceDays }); }
    catch (e: any) { alert(e?.message || "Could not schedule deletion."); } finally { setDelBusy(false); }
  }
  async function cancelDeletion() {
    setDelBusy(true);
    try { await api.cancelAccountDeletion(); const r = await api.dataStatus(); setDelState(r.deletion); }
    catch (e: any) { alert(e?.message || "Could not cancel."); } finally { setDelBusy(false); }
  }

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);
  useEffect(() => { if (user) api.getEmailPrefs().then((r) => setEmailNotif(r?.emailNotifications ?? true)).catch(() => {}); }, [user]);
  useEffect(() => {
    if (!user || !pushSupported()) return;
    api.pushStatus().then((r) => setPushInfo(r)).catch(() => {});
    navigator.serviceWorker?.getRegistration().then((reg) => reg?.pushManager.getSubscription()).then((s) => setPushOn(!!s)).catch(() => {});
  }, [user]);

  async function togglePush() {
    setPushBusy(true); setPushMsg(null);
    try {
      if (pushOn) { await disablePush(); setPushOn(false); setPushMsg("Push turned off for this device."); }
      else {
        const r = await enablePush();
        if (r.ok) { setPushOn(true); setPushMsg("Push enabled on this device."); }
        else setPushMsg(r.reason === "denied" ? "Permission was blocked in your browser." : r.reason === "not_configured" ? "Push isn't configured on the server yet." : "Couldn't enable push on this browser.");
      }
    } catch (e: any) { setPushMsg(e?.message || "Push action failed."); }
    finally { setPushBusy(false); }
  }
  async function testPush() {
    setPushBusy(true); setPushMsg(null);
    try { const r = await api.pushTest(); setPushMsg(`Test push sent to ${r?.delivered ?? 0} device(s).`); }
    catch (e: any) { setPushMsg(e?.message || "Could not send test."); }
    finally { setPushBusy(false); }
  }

  if (loading || !user) return <div className="container-x py-20 text-center text-mut">Loading…</div>;

  async function changeEmail(e: React.FormEvent) {
    e.preventDefault(); setEmailErr(null); setEmailPending(null); setEmailConfirmUrl(null);
    setEmailBusy(true);
    try {
      const r = await api.requestEmailChange(newEmail.trim(), emailPw);
      setEmailPending(r?.pendingEmail || newEmail.trim());
      if (r?.confirmUrl) setEmailConfirmUrl(r.confirmUrl.replace(/^https?:\/\/[^/]+/, ""));
      setNewEmail(""); setEmailPw("");
    } catch (e: any) { setEmailErr(e?.message || "Could not start the email change."); }
    finally { setEmailBusy(false); }
  }

  async function changePassword(e: React.FormEvent) {
    e.preventDefault(); setErr(null); setDone(false);
    if (next.length < 8) { setErr("New password must be at least 8 characters."); return; }
    if (next !== confirm) { setErr("New passwords don't match."); return; }
    setBusy(true);
    try { await api.changePassword(cur, next); setDone(true); setCur(""); setNext(""); setConfirm(""); }
    catch (e: any) { setErr(e?.message || "Could not change your password."); }
    finally { setBusy(false); }
  }

  return (
    <div className="container-x py-6 max-w-2xl">
      <h1 className="text-2xl font-extrabold text-navy mb-1">Settings</h1>
      <p className="text-mut text-sm mb-5">Manage your account.</p>

      <div className="card p-5 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-navy text-white grid place-items-center font-extrabold shrink-0">{(user.firstName?.[0] || "") + (user.lastName?.[0] || "")}</div>
          <div className="min-w-0">
            <div className="font-bold text-navy truncate">{user.firstName} {user.lastName}</div>
            <div className="text-sm text-mut truncate">{user.email}</div>
          </div>
          <Link href={`/u/${user.id}`} className="btn-outline ml-auto h-9 shrink-0"><UserCircle className="w-4 h-4" />Public profile</Link>
        </div>
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-line">
          {(user.capabilities || []).map((c) => <span key={c} className="chip bg-brand-green/10 text-brand-greendark"><Check className="w-3.5 h-3.5" />{ACTIVITY_LABELS[c] || c}</span>)}
        </div>
        <button onClick={async () => { await logout(); navigate("/"); }} className="btn-outline text-brand-red w-full mt-4 sm:w-auto"><LogOut className="w-4 h-4" />Log Out</button>
      </div>

      <div className="card p-5 mb-4">
        <h3 className="font-bold text-navy mb-3 flex items-center gap-2"><UserCircle className="w-4 h-4" />Personal information</h3>
        <form onSubmit={saveName} className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="label">First name</label><input className="field" value={nameForm.firstName} onChange={(e) => setNameForm({ ...nameForm, firstName: e.target.value })} /></div>
            <div><label className="label">Last name</label><input className="field" value={nameForm.lastName} onChange={(e) => setNameForm({ ...nameForm, lastName: e.target.value })} /></div>
          </div>
          <p className="text-xs text-mut">Your legal name is identity information. Changing it will be reviewed by our team (re-KYC); your verified status stays active during review.</p>
          {nameErr && <p className="text-sm text-brand-red flex items-center gap-1"><AlertCircle className="w-4 h-4" />{nameErr}</p>}
          {nameMsg && <p className="text-sm text-brand-green flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />{nameMsg}</p>}
          <button type="submit" disabled={nameBusy} className="btn-red disabled:opacity-60" data-testid="save-name">{nameBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}Save name</button>
        </form>
      </div>

      <div className="card p-5 mb-4">
        <h3 className="font-bold text-navy mb-3 flex items-center gap-2"><Mail className="w-4 h-4" />Change email</h3>
        {emailPending ? (
          <div className="text-sm">
            <p className="text-brand-green flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />Confirmation sent to <b>{emailPending}</b>. It becomes your sign-in email once you confirm from that address.</p>
            {emailConfirmUrl && (
              <p className="text-mut mt-2">Email delivery isn't live on UAT yet — <Link href={emailConfirmUrl} className="text-brand-red font-semibold underline">confirm the change now</Link>.</p>
            )}
          </div>
        ) : (
          <form onSubmit={changeEmail} className="space-y-3">
            <p className="text-sm text-mut">Current: <b>{user.email}</b></p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="label">New email</label><input className="field" type="email" required value={newEmail} onChange={(e) => setNewEmail(e.target.value)} autoComplete="email" /></div>
              <div><label className="label">Current password</label><input className="field" type="password" required value={emailPw} onChange={(e) => setEmailPw(e.target.value)} autoComplete="current-password" /></div>
            </div>
            {emailErr && <p className="text-sm text-brand-red flex items-center gap-1"><AlertCircle className="w-4 h-4" />{emailErr}</p>}
            <button type="submit" disabled={emailBusy} className="btn-outline disabled:opacity-60">{emailBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}Send confirmation</button>
          </form>
        )}
      </div>

      <div className="card p-5 mb-4">
        <h3 className="font-bold text-navy mb-3 flex items-center gap-2"><Bell className="w-4 h-4" />Notifications</h3>
        <label className="flex items-center justify-between gap-3 cursor-pointer">
          <span className="text-sm text-navy-700">Email me about important events (verifications, payments, hires, contract updates).</span>
          <input type="checkbox" className="w-5 h-5 accent-brand-red shrink-0" checked={emailNotif} disabled={emailNotifBusy}
            onChange={async (e) => { const v = e.target.checked; setEmailNotif(v); setEmailNotifBusy(true); try { await api.setEmailPrefs(v); } catch { setEmailNotif(!v); } finally { setEmailNotifBusy(false); } }} />
        </label>

        {/* Increment 36: browser push */}
        <div className="mt-4 pt-4 border-t border-line">
          {!pushSupported() ? (
            <p className="text-sm text-mut">Browser push notifications aren't supported on this device/browser.</p>
          ) : pushInfo && !pushInfo.configured ? (
            <p className="text-sm text-mut">Browser push isn't enabled on this server yet.</p>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-navy-700">Browser push notifications {pushOn ? <b className="text-brand-green">on</b> : "off"} for this device.</span>
              <div className="flex gap-2">
                {pushOn && <button onClick={testPush} disabled={pushBusy} className="btn-ghost h-8 px-3 text-xs border border-line disabled:opacity-50">Send test</button>}
                <button onClick={togglePush} disabled={pushBusy} className={`h-8 px-3 text-xs rounded-lg font-semibold disabled:opacity-50 ${pushOn ? "btn-outline text-brand-red" : "btn-red"}`}>
                  {pushBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : pushOn ? "Turn off" : "Enable push"}
                </button>
              </div>
            </div>
          )}
          {pushMsg && <p className="text-xs text-mut mt-2">{pushMsg}</p>}
        </div>
      </div>

      <div className="card p-5">
        <h3 className="font-bold text-navy mb-3 flex items-center gap-2"><KeyRound className="w-4 h-4" />Change password</h3>
        <form onSubmit={changePassword} className="space-y-3">
          <div><label className="label">Current password</label><input className="field" type="password" value={cur} onChange={(e) => setCur(e.target.value)} autoComplete="current-password" /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="label">New password</label>
              <div className="relative">
                <input className="field pr-11" type={showNew ? "text" : "password"} value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
                <button type="button" onClick={() => setShowNew((v) => !v)} aria-label={showNew ? "Hide password" : "Show password"} className="absolute inset-y-0 right-0 px-3 flex items-center text-mut hover:text-navy">{showNew ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
              </div>
            </div>
            <div><label className="label">Confirm new password</label>
              <div className="relative">
                <input className="field pr-11" type={showConf ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
                <button type="button" onClick={() => setShowConf((v) => !v)} aria-label={showConf ? "Hide password" : "Show password"} className="absolute inset-y-0 right-0 px-3 flex items-center text-mut hover:text-navy">{showConf ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
              </div>
            </div>
          </div>
          {err && <p className="text-sm text-brand-red flex items-center gap-1"><AlertCircle className="w-4 h-4" />{err}</p>}
          {done && <p className="text-sm text-brand-green flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />Password updated.</p>}
          <button type="submit" disabled={busy} className="btn-red disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}Update password</button>
        </form>
      </div>

      <div className="card p-5 mt-4" data-testid="data-rights">
        <h3 className="font-bold text-navy mb-1 flex items-center gap-2"><Download className="w-4 h-4" />Your data</h3>
        <p className="text-sm text-mut mb-3">Download a copy of everything associated with your account, or permanently delete your account.</p>
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={downloadExport} disabled={exportBusy} className="btn-outline disabled:opacity-60" data-testid="export-btn">
            {exportBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}Download my data
          </button>
          {exportMsg && <span className="text-sm text-mut">{exportMsg}</span>}
        </div>

        <div className="border-t border-line mt-4 pt-4">
          {delState?.scheduled ? (
            <div>
              <p className="text-sm text-brand-red font-semibold flex items-center gap-2"><ShieldOff className="w-4 h-4" />Deletion scheduled{delState.scheduledAt ? ` for ${new Date(delState.scheduledAt).toLocaleDateString()}` : ""}.</p>
              <p className="text-xs text-mut mt-1">Your account will be permanently erased then. You can still cancel until that date.</p>
              <button onClick={cancelDeletion} disabled={delBusy} className="btn-navy mt-3 disabled:opacity-60" data-testid="cancel-delete-btn">{delBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}Cancel deletion</button>
            </div>
          ) : (
            <div>
              <p className="text-sm text-mut mb-2">Deleting your account removes your listings and anonymizes your data after a {delState?.graceDays ?? 7}-day grace period.</p>
              <button onClick={requestDeletion} disabled={delBusy} className="btn-outline text-brand-red disabled:opacity-60" data-testid="delete-btn">{delBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}Delete my account</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
